import { CSSProperties } from "react";
import { resolveVariableChipValues } from "../components/shared/tiptap/VariableChip";

export const formatNameToDisplay = (str?: string, nameOnly?: boolean) => {
  if (str) {
    const [initial, firstName, lastName] = str.split(" ");
    if (lastName) return `${!nameOnly ? initial : firstName} ${lastName}`;
    return `${!nameOnly ? initial : ""} ${lastName || firstName}`.trim();
  }
  return str || "--";
};

export const cssObjectToString = (obj: CSSProperties) => {
  return Object.entries(obj)
    .map(([key, value]) => {
      return `${key
        .split(/(?=[A-Z])/)
        .join("-")
        .toLowerCase()}:${value}`;
    })
    .join(";");
};

export const truncateFileName = (str: string) => {
  const parts = str.split(".");
  const name = parts.slice(0, parts.length - 1).join(".");
  const ending = parts[parts.length - 1];
  if (name.length < 11) return str;
  return `${name.slice(0, 4)}...${name.slice(name.length - 5)}.${ending}`;
};

export const truncateLongName = (str: string) => {
  const parts = str.split(",");
  const name = parts.slice(0, parts.length - 1).join(",");
  const ending = parts[parts.length - 1];
  if (name.length < 11) return str;
  return `${name.slice(0, 4)}...${name.slice(name.length - 5)}.${ending}`;
};

/** Check if an HTML string is visually empty (only tags, whitespace, or &nbsp;) */
export function isEmptyHtml(html: string | null | undefined): boolean {
  if (!html) return true;
  const stripped = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return stripped.length === 0;
}

/**
 * Returns '#fff' or '#000' depending on which gives better contrast
 * against the given hex background color.
 */
export function getContrastColor(hex: string): "#fff" | "#000" {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  // Relative luminance (WCAG formula)
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return L > 0.179 ? "#000" : "#fff";
}

/** Strip HTML tags and return plain text */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim();
}

/**
 * Normalizes rule-outs from various input shapes (array of strings or comma-separated string)
 * into a cleaned array of non-empty strings.
 */
export function normalizeRuleOuts(
  value: string | string[] | undefined | null
): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((s) => s.trim()).filter(Boolean);
  }
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Unified resolver for rich text display on reports.
 * 1. Resolves TipTap chips (precedence: data-variable-value > extraVars)
 * 2. Resolves bare {{...}} tokens and legacy [[...]] tokens from extraVars
 * 3. Final cleanup: ensures no raw template syntax matching /\{\{[^}]+\}\}/ or
 *    legacy [[...]] remains. Unresolved variables are replaced with an
 *    explicit user-facing [MISSING: VarName] marker.
 */
export function resolveRichTextForDisplay(
  html: string | null | undefined,
  extraVars: Record<string, string | null | undefined>
): string {
  if (!html) return "";

  // 1. Resolve chips
  let resolved = resolveVariableChipValues(html, extraVars);

  // 2. Resolve bare tokens and legacy tokens
  Object.entries(extraVars).forEach(([name, val]) => {
    if (val != null && val !== "") {
      // {{variable}}
      const braceRegex = new RegExp(`\\{\\{${name}\\}\\}`, "gi");
      resolved = resolved.replace(braceRegex, val);

      // [[VARIABLE]]
      const bracketRegex = new RegExp(`\\[\\[${name}\\]\\]`, "gi");
      resolved = resolved.replace(bracketRegex, val);
    }
  });

  // 3. Final cleanup: handle any remaining unresolved tokens or chips
  // Replace <span data-variable="status">{{status}}</span> with [MISSING: status]
  resolved = resolved.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>/gi,
    (_match, varName) => `[MISSING: ${varName.trim().replace(/\s+/g, "_")}]`
  );

  // Replace any remaining bare {{var}} or [[VAR]] tokens
  resolved = resolved.replace(
    /(\{\{|\{\[|\[\[)([^}\]]+)(\}\}|\}\]|\]\])/g,
    (_match, _start, varName) =>
      `[MISSING: ${varName.trim().replace(/\s+/g, "_")}]`
  );

  return resolved;
}
